import React from "react";
import { safeLocalStorage } from '@/lib/storage-utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Settings,
  Sliders,
  LayoutDashboard,
  Plus,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Pencil,
  Check,
  X,
  Lock,
  Loader2,
  Pipette,
  Tag,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { updateBoardsList } from '@/lib/boards';
import { useBoardBackgroundEditor } from '@/hooks/useBoardBackgroundEditor';
import { compressImageToDataUrl } from '@/lib/image';
import { toast } from "sonner";

export type HeaderConfig = {
  title: string;
  description: string;
  background: { type: "gradient" | "color" | "image"; value: string; overlay?: string };
  hideBanner?: boolean;
  enableUpvotes?: boolean;
};

const DEFAULT_CONFIG: HeaderConfig = {
  title: "Untitled Board",
  description: "Plan, prioritize, and track work visually. Create tasks, drag across states, and archive completed items with ease.",
  background: {
    type: "gradient",
    value: "linear-gradient(135deg, hsl(var(--brand) / 0.12), hsl(var(--primary) / 0.06))",
  },
  hideBanner: true,
  enableUpvotes: false,
};

const LS_KEY_PREFIX = "header-config-v1";
const PROTECTED_COLUMN_TITLES = new Set(["Open", "To Do", "In Progress", "Done"]);

export function useHeaderConfig(slug?: string): [HeaderConfig, (cfg: HeaderConfig) => void] {
  const lsKey = slug ? `${LS_KEY_PREFIX}-${slug}` : LS_KEY_PREFIX;
  const [cfg, setCfg] = React.useState<HeaderConfig>(() => {
    const from = safeLocalStorage.getItem(lsKey);
    return from ? (JSON.parse(from) as HeaderConfig) : DEFAULT_CONFIG;
  });

  React.useEffect(() => {
    const from = safeLocalStorage.getItem(lsKey);
    setCfg(from ? (JSON.parse(from) as HeaderConfig) : DEFAULT_CONFIG);
  }, [lsKey]);

  const save = React.useCallback((next: HeaderConfig) => {
    setCfg(next);
    safeLocalStorage.setItem(lsKey, JSON.stringify(next));
  }, [lsKey]);

  return [cfg, save];
}

const COLOR_PRESETS = [
  { name: "Default", value: "", bgClass: "bg-muted/70", hex: "#f1f5f9", borderClass: "border-border" },
  { name: "Blue", value: "bg-board-column-blue", bgClass: "bg-blue-100 dark:bg-blue-950/70", hex: "#dbeafe", borderClass: "border-blue-300 dark:border-blue-800" },
  { name: "Green", value: "bg-board-column-green", bgClass: "bg-emerald-100 dark:bg-emerald-950/70", hex: "#dcfce7", borderClass: "border-emerald-300 dark:border-emerald-800" },
  { name: "Red", value: "bg-board-column-red", bgClass: "bg-rose-100 dark:bg-rose-950/70", hex: "#fee2e2", borderClass: "border-rose-300 dark:border-rose-800" },
  { name: "Accent", value: "bg-board-column-primary", bgClass: "bg-purple-100 dark:bg-purple-950/70", hex: "#f3e8ff", borderClass: "border-purple-300 dark:border-purple-800" },
  { name: "Yellow", value: "bg-board-column-yellow", bgClass: "bg-amber-100 dark:bg-amber-950/70", hex: "#fef9c3", borderClass: "border-amber-300 dark:border-amber-800" },
  { name: "Orange", value: "bg-board-column-orange", bgClass: "bg-orange-100 dark:bg-orange-950/70", hex: "#ffedd5", borderClass: "border-orange-300 dark:border-orange-800" },
];

const getColumnColorStyle = (bgColor?: string) => {
  const preset = COLOR_PRESETS.find((p) => p.value === bgColor);
  if (preset) {
    return { bgClass: preset.bgClass, borderClass: preset.borderClass, style: undefined };
  }
  if (bgColor && (bgColor.startsWith("#") || bgColor.startsWith("rgb") || bgColor.startsWith("hsl"))) {
    return { bgClass: "", borderClass: "border-border/80", style: { backgroundColor: bgColor } };
  }
  return { bgClass: "bg-muted/70", borderClass: "border-border", style: undefined };
};

interface InlineColorPaletteProps {
  currentColor?: string;
  onSelectColor: (color: string) => void;
  size?: "sm" | "md";
}

const InlineColorPalette: React.FC<InlineColorPaletteProps> = ({
  currentColor = "",
  onSelectColor,
  size = "sm",
}) => {
  const isCustomColorActive = Boolean(
    currentColor && !COLOR_PRESETS.some((p) => p.value === currentColor)
  );

  const circleCls = size === "sm" ? "w-5 h-5" : "w-6 h-6";
  const iconCls = size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3";
  const innerPipetteCls = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
  const pipetteIconCls = size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2";

  return (
    <div className="flex items-center gap-1.5 py-0.5">
      {COLOR_PRESETS.map((preset) => {
        const isSelected = (!currentColor && !preset.value) || currentColor === preset.value;
        return (
          <button
            key={preset.name}
            type="button"
            onClick={() => onSelectColor(preset.value)}
            className={`relative ${circleCls} rounded-full border transition-all hover:scale-115 flex items-center justify-center shrink-0 cursor-pointer shadow-xs ${preset.bgClass} ${preset.borderClass} ${
              isSelected
                ? "ring-2 ring-primary ring-offset-1 ring-offset-background scale-105 border-primary"
                : "hover:ring-1 hover:ring-primary/40"
            }`}
            title={preset.name}
          >
            {isSelected && (
              <Check className={`${iconCls} text-foreground drop-shadow-xs`} strokeWidth={3} />
            )}
          </button>
        );
      })}

      {/* Custom Color Option */}
      <label
        className={`relative ${circleCls} rounded-full border transition-all hover:scale-115 flex items-center justify-center shrink-0 cursor-pointer shadow-xs ${
          isCustomColorActive
            ? "ring-2 ring-primary ring-offset-1 ring-offset-background scale-105 border-primary"
            : "border-border/60 hover:ring-1 hover:ring-primary/40"
        }`}
        style={
          isCustomColorActive
            ? { backgroundColor: currentColor }
            : {
                background:
                  "conic-gradient(from 180deg at 50% 50%, #f43f5e 0deg, #fbbf24 60deg, #34d399 120deg, #38bdf8 180deg, #818cf8 240deg, #c084fc 300deg, #f43f5e 360deg)",
              }
        }
        title="Custom color"
      >
        <input
          type="color"
          value={currentColor?.startsWith("#") ? currentColor : "#3b82f6"}
          onChange={(e) => onSelectColor(e.target.value)}
          className="sr-only"
        />
        {isCustomColorActive ? (
          <Check className={`${iconCls} text-white drop-shadow-md`} strokeWidth={3} />
        ) : (
          <div className={`${innerPipetteCls} rounded-full bg-background flex items-center justify-center shadow-xs`}>
            <Pipette className={`${pipetteIconCls} text-foreground`} />
          </div>
        )}
      </label>
    </div>
  );
};

export type HeaderSettingsDialogProps = {
  value: HeaderConfig;
  onSave: (cfg: HeaderConfig) => void | Promise<void>;
  currentSlug?: string;
  iconOnly?: boolean;
  // Columns / Stages management
  columnOrder?: string[];
  columnMeta?: Record<string, { id: string; title: string; bgColor?: string; isWon?: boolean; isLost?: boolean }>;
  columnTasks?: Record<string, string[]>;
  onAddColumn?: (title: string, bgColor?: string) => Promise<void> | void;
  onDeleteColumn?: (id: string) => Promise<void> | void;
  onReorderColumns?: (newOrder: string[]) => Promise<void> | void;
  onUpdateColumnTitle?: (id: string, title: string) => Promise<void> | void;
  onUpdateColumnBgColor?: (id: string, bgColor: string) => Promise<void> | void;
  defaultTab?: "general" | "columns" | "fields" | "tags";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  itemType?: "column" | "stage";
  protectedTitles?: Set<string>;
  dialogTitle?: string;
};

export const HeaderSettingsDialog: React.FC<HeaderSettingsDialogProps> = ({
  value,
  onSave,
  currentSlug,
  iconOnly = false,
  columnOrder = [],
  columnMeta = {},
  columnTasks = {},
  onAddColumn,
  onDeleteColumn,
  onReorderColumns,
  onUpdateColumnTitle,
  onUpdateColumnBgColor,
  defaultTab = "general",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  itemType = "column",
  protectedTitles,
  dialogTitle,
}) => {
  const isStage = itemType === "stage";
  const itemSingular = isStage ? "stage" : "column";
  const ItemSingular = isStage ? "Stage" : "Column";
  const itemPlural = isStage ? "stages" : "columns";
  const ItemPlural = isStage ? "Stages" : "Columns";
  const unitSingular = isStage ? "deal" : "task";
  const unitPlural = isStage ? "deals" : "tasks";
  const effectiveProtectedTitles = protectedTitles ?? (isStage ? new Set(["Won", "Lost"]) : PROTECTED_COLUMN_TITLES);

  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange ?? (() => {})) : setInternalOpen;

  const initialTab = isStage ? (defaultTab === "general" ? "columns" : defaultTab) : defaultTab;
  const [activeTab, setActiveTab] = React.useState<string>(initialTab);
  const [draft, setDraft] = React.useState<HeaderConfig>(value);
  const [isSavingGeneral, setIsSavingGeneral] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setActiveTab(isStage ? (defaultTab === "general" ? "columns" : defaultTab) : defaultTab);
    }
  }, [open, defaultTab, isStage]);

  // Column / Stage management state
  const [localColumnOrder, setLocalColumnOrder] = React.useState<string[]>(columnOrder);
  const [newColumnTitle, setNewColumnTitle] = React.useState("");
  const [newColumnColor, setNewColumnColor] = React.useState("");
  const newColorStyle = getColumnColorStyle(newColumnColor);
  const [isAddingColumn, setIsAddingColumn] = React.useState(false);
  const [editingColumnId, setEditingColumnId] = React.useState<string | null>(null);
  const [editingColumnTitle, setEditingColumnTitle] = React.useState("");
  const [columnToDelete, setColumnToDelete] = React.useState<{ id: string; title: string; taskCount: number } | null>(null);
  const [isDeletingColumn, setIsDeletingColumn] = React.useState(false);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = React.useState<number | null>(null);

  // Style tokens
  const contentCls = 'bg-card border-border text-card-foreground';
  const labelCls = 'text-foreground font-medium text-sm';
  const inputCls = 'bg-background border-input text-foreground placeholder:text-muted-foreground';
  const selectTriggerCls = inputCls;
  const rangeCls = 'accent-workspace-accent bg-muted rounded h-2';
  const primaryBtn = 'bg-workspace-accent text-white hover:bg-workspace-accent-hover';

  const {
    gradFrom,
    gradTo,
    gradAngle,
    imagePreview,
    overlayOpacity,
    setImagePreview,
    updateGradient,
    handleOverlayChange,
    syncFromBackground,
  } = useBoardBackgroundEditor();

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  React.useEffect(() => {
    if (open) {
      setLocalColumnOrder(columnOrder);
    }
  }, [columnOrder, open]);

  React.useEffect(() => {
    syncFromBackground(draft.background);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.background.type]);

  const onUpdateGradient = (from: string, to: string, angle: number) => {
    updateGradient(from, to, angle);
    setDraft({ ...draft, background: { type: "gradient", value: `linear-gradient(${angle}deg, ${from}, ${to})` } });
  };

  const handleImageUpload = async (file: File | null) => {
    if (!file) return;
    try {
      const dataUrl = await compressImageToDataUrl(file, { maxWidth: 1800, quality: 0.85, mimeType: 'image/jpeg' });
      setImagePreview(dataUrl);
      const overlay = overlayOpacity > 0 ? `rgba(0, 0, 0, ${overlayOpacity / 100})` : undefined;
      setDraft({ ...draft, background: { type: "image", value: dataUrl, overlay } });
    } catch (err) {
      console.error('Image upload failed:', err);
      toast.error('Failed to process image');
    }
  };

  const onOverlayChange = (opacity: number) => {
    handleOverlayChange(opacity);
    const overlay = opacity > 0 ? `rgba(0, 0, 0, ${opacity / 100})` : undefined;
    setDraft({ ...draft, background: { ...draft.background, overlay } });
  };

  const handleSaveGeneral = async () => {
    try {
      setIsSavingGeneral(true);
      await onSave({ ...draft, background: { ...draft.background } });
      
      if (currentSlug) {
        updateBoardsList(currentSlug, draft);
      }
      
      toast.success(isStage ? 'Pipeline settings updated' : 'Board settings updated');
      setOpen(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(isStage ? 'Failed to save pipeline settings' : 'Failed to save board settings');
    } finally {
      setIsSavingGeneral(false);
    }
  };

  // Add Column / Stage
  const handleAddColumnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = newColumnTitle.trim();
    if (!title || !onAddColumn) return;

    try {
      setIsAddingColumn(true);
      await onAddColumn(title, newColumnColor || undefined);
      setNewColumnTitle("");
      setNewColumnColor("");
      toast.success(`${ItemSingular} "${title}" added`);
    } catch (error) {
      console.error(`Error adding ${itemSingular}:`, error);
      toast.error(`Failed to add ${itemSingular}`);
    } finally {
      setIsAddingColumn(false);
    }
  };

  const handleUpdateColumnColor = async (colId: string, newColor: string) => {
    try {
      if (onUpdateColumnBgColor) {
        await onUpdateColumnBgColor(colId, newColor);
      }
      toast.success(`${ItemSingular} color updated`);
    } catch (error) {
      console.error(`Error updating ${itemSingular} color:`, error);
      toast.error(`Failed to update ${itemSingular} color`);
    }
  };

  // Rename Column / Stage
  const handleStartRename = (colId: string, currentTitle: string) => {
    setEditingColumnId(colId);
    setEditingColumnTitle(currentTitle);
  };

  const handleSaveRename = async (colId: string) => {
    const trimmed = editingColumnTitle.trim();
    if (!trimmed) {
      setEditingColumnId(null);
      return;
    }
    try {
      if (onUpdateColumnTitle) {
        await onUpdateColumnTitle(colId, trimmed);
      }
      toast.success(`${ItemSingular} renamed to "${trimmed}"`);
    } catch (error) {
      console.error(`Error updating ${itemSingular} title:`, error);
      toast.error(`Failed to rename ${itemSingular}`);
    } finally {
      setEditingColumnId(null);
    }
  };

  // Reorder Column / Stage via arrows or drag
  const handleMoveColumn = async (currentIndex: number, direction: -1 | 1) => {
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= localColumnOrder.length) return;

    const newOrder = [...localColumnOrder];
    const [moved] = newOrder.splice(currentIndex, 1);
    newOrder.splice(targetIndex, 0, moved);

    setLocalColumnOrder(newOrder);
    try {
      if (onReorderColumns) {
        await onReorderColumns(newOrder);
      }
    } catch (error) {
      console.error(`Error reordering ${itemPlural}:`, error);
      toast.error(`Failed to reorder ${itemPlural}`);
      setLocalColumnOrder(columnOrder); // revert
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData("text/plain", `${index}`);
    e.dataTransfer.effectAllowed = "move";
    setDraggedIndex(index);
  };

  const handleItemDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const isTopHalf = offsetY < rect.height / 2;
    const target = isTopHalf ? index : index + 1;
    if (dropTargetIndex !== target) {
      setDropTargetIndex(target);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDropToTarget = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = draggedIndex;
    setDraggedIndex(null);
    setDropTargetIndex(null);

    if (sourceIndex === null) return;
    // If dropping right before or right after own slot, no movement needed
    if (targetIndex === sourceIndex || targetIndex === sourceIndex + 1) return;

    const newOrder = [...localColumnOrder];
    const [moved] = newOrder.splice(sourceIndex, 1);
    const insertAt = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
    newOrder.splice(insertAt, 0, moved);

    setLocalColumnOrder(newOrder);
    try {
      if (onReorderColumns) {
        await onReorderColumns(newOrder);
      }
    } catch (error) {
      console.error(`Error reordering ${itemPlural}:`, error);
      toast.error(`Failed to reorder ${itemPlural}`);
      setLocalColumnOrder(columnOrder); // revert
    }
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!columnToDelete || !onDeleteColumn) return;
    try {
      setIsDeletingColumn(true);
      await onDeleteColumn(columnToDelete.id);
      toast.success(`${ItemSingular} "${columnToDelete.title}" deleted`);
      setColumnToDelete(null);
    } catch (error) {
      console.error(`Error deleting ${itemSingular}:`, error);
      toast.error(`Failed to delete ${itemSingular}`);
    } finally {
      setIsDeletingColumn(false);
    }
  };

  const totalColumns = localColumnOrder.length;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {iconOnly ? (
            <button
              type="button"
              aria-label={isStage ? "Pipeline settings" : "Board settings"}
              title={isStage ? "Pipeline settings" : "Board settings"}
              className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shadow-none"
            >
              <Settings className="w-4 h-4" />
            </button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              aria-label={isStage ? "Pipeline settings" : "Board settings"}
              className="bg-card hover:bg-accent border-border"
            >
              <Settings className="h-4 w-4 mr-2" /> {isStage ? "Pipeline Settings" : "Board Settings"}
            </Button>
          )}
        </DialogTrigger>

        <DialogContent className={`max-w-2xl max-h-[90vh] md:max-h-[90vh] max-md:max-w-full max-md:w-full max-md:h-full max-md:max-h-screen max-md:rounded-none max-md:inset-0 max-md:translate-x-0 max-md:translate-y-0 max-md:top-0 max-md:left-0 overflow-y-auto border ${contentCls}`}>
          <DialogDescription className="sr-only">
            {isStage ? "Pipeline settings and stage management" : "Board settings and column management"}
          </DialogDescription>
          <DialogHeader className="max-md:border-b max-md:border-border max-md:pb-3">
            <DialogTitle className="text-xl font-semibold text-foreground">
              {dialogTitle || (isStage ? "Pipeline Settings" : "Board Settings")}
            </DialogTitle>
          </DialogHeader>

          {/* Navigation Tabs: Stage Settings, Field Settings, Tags (Pipelines) or General, Columns (Boards) */}
          <Tabs value={activeTab} onValueChange={(val) => setActiveTab(val)} className="w-full">
            <TabsList className={`grid w-full ${isStage ? "grid-cols-3" : "grid-cols-2"} h-10 p-1 bg-muted/80 rounded-lg border border-border/50`}>
              {isStage ? (
                <>
                  <TabsTrigger
                    value="fields"
                    className="h-8 flex items-center justify-center gap-2 text-sm font-medium rounded-md transition-all text-muted-foreground hover:text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
                  >
                    <Sliders className="h-4 w-4" />
                    <span>Field Settings</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="tags"
                    className="h-8 flex items-center justify-center gap-2 text-sm font-medium rounded-md transition-all text-muted-foreground hover:text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
                  >
                    <Tag className="h-4 w-4" />
                    <span>Tags</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="columns"
                    className="h-8 flex items-center justify-center gap-2 text-sm font-medium rounded-md transition-all text-muted-foreground hover:text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs group"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Stage Settings</span>
                    {totalColumns > 0 && (
                      <span className="ml-1.5 px-2 py-0.5 text-xs font-semibold rounded-full bg-muted text-muted-foreground group-data-[state=active]:bg-workspace-accent/15 group-data-[state=active]:text-workspace-accent transition-colors">
                        {totalColumns}
                      </span>
                    )}
                  </TabsTrigger>
                </>
              ) : (
                <>
                  <TabsTrigger
                    value="general"
                    className="h-8 flex items-center justify-center gap-2 text-sm font-medium rounded-md transition-all text-muted-foreground hover:text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
                  >
                    <Sliders className="h-4 w-4" />
                    <span>General</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="columns"
                    className="h-8 flex items-center justify-center gap-2 text-sm font-medium rounded-md transition-all text-muted-foreground hover:text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs group"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>{ItemPlural}</span>
                    {totalColumns > 0 && (
                      <span className="ml-1.5 px-2 py-0.5 text-xs font-semibold rounded-full bg-muted text-muted-foreground group-data-[state=active]:bg-workspace-accent/15 group-data-[state=active]:text-workspace-accent transition-colors">
                        {totalColumns}
                      </span>
                    )}
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            {/* TAB 1: GENERAL (Only for Task Boards, removed for Pipelines) */}
            {!isStage && (
              <TabsContent value="general" className="mt-4 focus-visible:outline-none">
              <div className="grid gap-4 py-1">
                <div className="grid gap-2">
                  <Label className={labelCls} htmlFor="hdr-title">{isStage ? "Pipeline Title" : "Board Title"}</Label>
                  <Input
                    className={inputCls}
                    id="hdr-title"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    placeholder={isStage ? "Enter pipeline title" : "Enter board title"}
                  />
                </div>

                <div className="grid gap-2">
                  <Label className={labelCls} htmlFor="hdr-desc">Description</Label>
                  <Textarea
                    className={inputCls}
                    id="hdr-desc"
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder={isStage ? "Describe what this pipeline is used for..." : "Describe what this board is used for..."}
                    rows={3}
                  />
                </div>

                <div className="grid gap-2">
                  <Label className={labelCls}>Background Type</Label>
                  <Select
                    value={draft.background.type}
                    onValueChange={(val: "gradient" | "color" | "image") => {
                      if (val === "color") {
                        setDraft({ ...draft, background: { type: val, value: draft.background.value || "#e2e8f0" } });
                      } else if (val === "gradient") {
                        setDraft({ ...draft, background: { type: val, value: `linear-gradient(${gradAngle}deg, ${gradFrom}, ${gradTo})` } });
                      } else {
                        setDraft({ ...draft, background: { type: val, value: imagePreview ?? "" } });
                      }
                    }}
                  >
                    <SelectTrigger className={selectTriggerCls}>
                      <SelectValue placeholder="Select background type" />
                    </SelectTrigger>
                    <SelectContent className={contentCls}>
                      <SelectItem value="gradient">Gradient</SelectItem>
                      <SelectItem value="color">Solid Color</SelectItem>
                      <SelectItem value="image">Image Upload</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  {draft.background.type === "color" && (
                    <>
                      <Label className={labelCls} htmlFor="hdr-color">Pick Color</Label>
                      <div className="flex items-center gap-3">
                        <Input
                          id="hdr-color"
                          type="color"
                          value={draft.background.value}
                          onChange={(e) => setDraft({ ...draft, background: { type: "color", value: e.target.value } })}
                          className="h-10 w-16 p-0 border-0 rounded-md ring-0 outline-none bg-transparent cursor-pointer"
                        />
                        <Input
                          className={inputCls}
                          value={draft.background.value}
                          onChange={(e) => setDraft({ ...draft, background: { type: "color", value: e.target.value } })}
                          aria-label="Color hex"
                        />
                      </div>
                    </>
                  )}

                  {draft.background.type === "gradient" && (
                    <>
                      <Label className={labelCls}>Gradient Colors</Label>
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs text-muted-foreground w-10">From</span>
                          <Input
                            type="color"
                            value={gradFrom}
                            onChange={(e) => onUpdateGradient(e.target.value, gradTo, gradAngle)}
                            className="h-10 w-14 p-0 border-0 rounded-md ring-0 outline-none bg-transparent cursor-pointer"
                          />
                          <Input
                            className={inputCls}
                            value={gradFrom}
                            onChange={(e) => onUpdateGradient(e.target.value, gradTo, gradAngle)}
                            aria-label="From color"
                          />
                        </div>
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs text-muted-foreground w-10">To</span>
                          <Input
                            type="color"
                            value={gradTo}
                            onChange={(e) => onUpdateGradient(gradFrom, e.target.value, gradAngle)}
                            className="h-10 w-14 p-0 border-0 rounded-md ring-0 outline-none bg-transparent cursor-pointer"
                          />
                          <Input
                            className={inputCls}
                            value={gradTo}
                            onChange={(e) => onUpdateGradient(gradFrom, e.target.value, gradAngle)}
                            aria-label="To color"
                          />
                        </div>
                      </div>
                      <div className="mt-2 space-y-1.5">
                        <Label htmlFor="hdr-angle" className={labelCls}>Angle ({gradAngle}°)</Label>
                        <div className="flex items-center gap-3">
                          <Input
                            id="hdr-angle"
                            type="range"
                            min={0}
                            max={360}
                            value={gradAngle}
                            onChange={(e) => onUpdateGradient(gradFrom, gradTo, Number(e.target.value))}
                            className={rangeCls}
                          />
                          <Input
                            type="number"
                            className={`w-20 ${inputCls}`}
                            value={gradAngle}
                            onChange={(e) => onUpdateGradient(gradFrom, gradTo, Number(e.target.value))}
                            aria-label="Gradient angle"
                          />
                        </div>
                      </div>
                      <div
                        className="mt-3 h-16 rounded-md border border-border shadow-inner"
                        style={{ backgroundImage: `linear-gradient(${gradAngle}deg, ${gradFrom}, ${gradTo})` }}
                        aria-label="Gradient preview"
                      />
                    </>
                  )}

                  {draft.background.type === "image" && (
                    <>
                      <Label className={labelCls} htmlFor="hdr-image">Upload Banner Image</Label>
                      <Input
                        className={inputCls}
                        id="hdr-image"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files?.[0] ?? null)}
                      />
                      {imagePreview && (
                        <>
                          <div className="mt-2 rounded-md max-h-40 overflow-hidden relative border border-border">
                            <img src={imagePreview} alt="Header background preview" className="object-cover w-full h-full" />
                            {overlayOpacity > 0 && (
                              <div
                                className="absolute inset-0 bg-black"
                                style={{ opacity: overlayOpacity / 100 }}
                              />
                            )}
                          </div>
                          <div className="mt-3">
                            <Label className={labelCls} htmlFor="overlay-opacity">Overlay Darkness ({overlayOpacity}%)</Label>
                            <div className="flex items-center gap-3 mt-1">
                              <Input
                                id="overlay-opacity"
                                type="range"
                                min={0}
                                max={80}
                                value={overlayOpacity}
                                onChange={(e) => onOverlayChange(Number(e.target.value))}
                                className={rangeCls}
                              />
                              <Input
                                type="number"
                                className={`w-20 ${inputCls}`}
                                min={0}
                                max={80}
                                value={overlayOpacity}
                                onChange={(e) => onOverlayChange(Number(e.target.value))}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Upvote feature toggle */}
                <div className="flex items-center justify-between p-3.5 rounded-lg border border-border/80 bg-muted/20">
                  <div className="space-y-0.5">
                    <Label htmlFor="enable-upvotes" className={labelCls}>Task Upvoting</Label>
                    <p className="text-xs text-muted-foreground">
                      Allow members to upvote tasks and display upvote counts on tasks.
                    </p>
                  </div>
                  <Switch
                    id="enable-upvotes"
                    checked={Boolean(draft.enableUpvotes)}
                    onCheckedChange={(checked) => setDraft({ ...draft, enableUpvotes: checked })}
                  />
                </div>
              </div>

              <DialogFooter className="mt-6 pt-3 border-t border-border flex items-center justify-end gap-2">
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button className={primaryBtn} onClick={handleSaveGeneral} disabled={isSavingGeneral}>
                  {isSavingGeneral ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </DialogFooter>
            </TabsContent>
            )}

            {/* TAB 2: COLUMNS / STAGES LIST & MANAGEMENT */}
            <TabsContent value="columns" className="mt-4 space-y-4 focus-visible:outline-none">
              {/* Add Column / Stage Option */}
              <div className="p-3.5 rounded-lg border border-border/80 bg-muted/20 space-y-2.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="new-column-title" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Add New {ItemSingular}
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    {totalColumns} {totalColumns === 1 ? itemSingular : itemPlural} {isStage ? 'in pipeline' : 'on board'}
                  </span>
                </div>
                <form onSubmit={handleAddColumnSubmit} className="flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Input
                      id="new-column-title"
                      value={newColumnTitle}
                      onChange={(e) => setNewColumnTitle(e.target.value)}
                      placeholder={isStage ? "Enter stage name (e.g., Qualified, Proposal Sent, In Negotiation)..." : "Enter column name (e.g., In Review, QA, Approved)..."}
                      className={`h-9 pr-8 ${inputCls}`}
                      maxLength={50}
                    />
                    {newColumnTitle && (
                      <button
                        type="button"
                        onClick={() => setNewColumnTitle("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Color selection dropdown trigger on the same line */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="h-9 px-2.5 rounded-md border border-input bg-background hover:bg-accent/60 transition-colors flex items-center gap-2 shrink-0 cursor-pointer shadow-xs text-xs font-medium text-foreground"
                        title={`Select ${itemSingular} color`}
                        aria-label={`Select ${itemSingular} color`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full border shadow-2xs shrink-0 ${newColorStyle.bgClass} ${newColorStyle.borderClass}`}
                          style={newColorStyle.style}
                        />
                        <span>Color</span>
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" sideOffset={6} className="w-auto p-2 bg-card border-border shadow-lg rounded-xl z-[60]">
                      <div className="space-y-1.5">
                        <div className="text-[10px] font-semibold text-primary uppercase tracking-wider px-0.5">
                          {ItemSingular} Color
                        </div>
                        <InlineColorPalette
                          currentColor={newColumnColor}
                          onSelectColor={setNewColumnColor}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>

                  <Button
                    type="submit"
                    size="sm"
                    disabled={!newColumnTitle.trim() || isAddingColumn}
                    className="h-9 bg-workspace-accent text-white hover:bg-workspace-accent-hover font-medium shrink-0 px-3.5"
                  >
                    {isAddingColumn ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-1.5" /> Add {ItemSingular}
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Column / Stage list guidance */}
              <div className="flex items-center justify-between px-1">
                <p className="text-xs text-muted-foreground">
                  Drag items up or down to reorder, or use the arrow buttons.
                </p>
              </div>

              {/* Columns / Stages List View with Drop Zones */}
              <div
                className="space-y-1.5 max-h-[48vh] overflow-y-auto pr-1"
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDropTargetIndex(null);
                  }
                }}
              >
                {localColumnOrder.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm border border-dashed rounded-lg">
                    No {itemPlural} found. Add one above to get started.
                  </div>
                ) : (
                  <>
                    {/* Drop zone before the first item */}
                    {draggedIndex !== null && dropTargetIndex === 0 && draggedIndex !== 0 && (
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(e) => handleDropToTarget(e, 0)}
                        className="h-10 my-1 rounded-lg border-2 border-dashed border-workspace-accent bg-workspace-accent/10 flex items-center justify-center gap-2 text-xs font-semibold text-workspace-accent transition-all duration-150 animate-in fade-in select-none shadow-xs"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-workspace-accent animate-ping" />
                        <span>Drop {itemSingular} here</span>
                      </div>
                    )}

                    {localColumnOrder.map((colId, index) => {
                      const meta = columnMeta[colId] || { id: colId, title: `Untitled ${ItemSingular}` };
                      const isProtected = effectiveProtectedTitles.has(meta.title) || Boolean(meta.isWon || meta.isLost);
                      const taskCount = columnTasks[colId]?.length || 0;
                      const isEditing = editingColumnId === colId;
                      const isDragged = draggedIndex === index;
                      const colorStyle = getColumnColorStyle(meta.bgColor);

                      return (
                        <React.Fragment key={colId}>
                          <div
                            draggable={!isEditing}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleItemDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            onDrop={(e) => {
                              if (dropTargetIndex !== null) {
                                handleDropToTarget(e, dropTargetIndex);
                              }
                            }}
                            className={`group relative flex items-center justify-between p-2.5 rounded-lg border transition-all select-none ${
                              isDragged
                                ? "opacity-30 border-dashed border-muted-foreground/40 bg-muted/20 scale-[0.99]"
                                : "border-border/70 bg-card hover:bg-accent/40 hover:border-border"
                            }`}
                          >
                            {/* Left: Drag Handle, Number, Color Swatch, Title / Inline Editor, Badges */}
                            <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                              {/* Drag handle */}
                              <div
                                className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground/70 group-hover:text-foreground rounded hover:bg-muted/60 transition-colors shrink-0"
                                title="Drag up or down to reorder"
                              >
                                <GripVertical className="h-4 w-4" />
                              </div>

                              {/* Index badge */}
                              <span className="text-xs font-mono font-medium text-muted-foreground w-5 text-center shrink-0">
                                #{index + 1}
                              </span>

                              {/* Color swatch Popover */}
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className={`w-5 h-5 rounded-full border transition-all hover:scale-115 flex items-center justify-center shrink-0 cursor-pointer shadow-xs ${colorStyle.bgClass} ${colorStyle.borderClass} hover:ring-2 hover:ring-primary/40`}
                                    style={colorStyle.style}
                                    title={`Change ${itemSingular} color`}
                                    aria-label={`Change color for ${meta.title}`}
                                  />
                                </PopoverTrigger>
                                <PopoverContent align="start" sideOffset={6} className="w-auto p-2 bg-card border-border shadow-lg rounded-xl z-[60]">
                                  <div className="space-y-1.5">
                                    <div className="text-[10px] font-semibold text-primary uppercase tracking-wider px-0.5">
                                      {ItemSingular} Color
                                    </div>
                                    <InlineColorPalette
                                      currentColor={meta.bgColor}
                                      onSelectColor={(newColor) => handleUpdateColumnColor(colId, newColor)}
                                    />
                                  </div>
                                </PopoverContent>
                              </Popover>

                              {/* Title or Inline Rename Input */}
                              {isEditing ? (
                                <div className="flex items-center gap-1.5 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                                  <Input
                                    value={editingColumnTitle}
                                    onChange={(e) => setEditingColumnTitle(e.target.value)}
                                    className={`h-7 text-sm py-0 px-2 ${inputCls}`}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveRename(colId);
                                      if (e.key === 'Escape') setEditingColumnId(null);
                                    }}
                                  />
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-500/10"
                                    onClick={() => handleSaveRename(colId)}
                                    title="Save name"
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                    onClick={() => setEditingColumnId(null)}
                                    title="Cancel"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span
                                    className="text-sm font-medium text-foreground truncate cursor-pointer hover:underline"
                                    title="Click to rename"
                                    onClick={() => handleStartRename(colId, meta.title)}
                                  >
                                    {meta.title}
                                  </span>

                                  {/* Count badge */}
                                  <span className="text-[11px] text-muted-foreground px-2 py-0.5 rounded-full bg-muted/60 shrink-0 font-medium">
                                    {taskCount} {taskCount === 1 ? unitSingular : unitPlural}
                                  </span>

                                  {/* Protected badge */}
                                  {isProtected && (
                                    <span className="text-[10px] uppercase font-semibold text-muted-foreground px-1.5 py-0.5 rounded bg-muted/50 border border-border/50 flex items-center gap-1 shrink-0">
                                      <Lock className="h-2.5 w-2.5" /> Default
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Right: Move Up / Down, Rename, Delete */}
                            <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                              {/* Move Up */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground disabled:opacity-30"
                                disabled={index === 0}
                                onClick={() => handleMoveColumn(index, -1)}
                                title="Move up"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>

                              {/* Move Down */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground disabled:opacity-30"
                                disabled={index === localColumnOrder.length - 1}
                                onClick={() => handleMoveColumn(index, 1)}
                                title="Move down"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>

                              {/* Rename button */}
                              {!isEditing && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  onClick={() => handleStartRename(colId, meta.title)}
                                  title={`Rename ${itemSingular}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              )}

                              {/* Delete button */}
                              {isProtected ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  disabled
                                  className="h-7 w-7 text-muted-foreground/30 cursor-not-allowed"
                                  title={`Default ${itemPlural} cannot be deleted`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  onClick={() => setColumnToDelete({ id: colId, title: meta.title, taskCount })}
                                  title={`Delete "${meta.title}"`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Drop zone after this item */}
                          {draggedIndex !== null &&
                            dropTargetIndex === index + 1 &&
                            dropTargetIndex !== draggedIndex &&
                            dropTargetIndex !== draggedIndex + 1 && (
                              <div
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = "move";
                                }}
                                onDrop={(e) => handleDropToTarget(e, index + 1)}
                                className="h-10 my-1 rounded-lg border-2 border-dashed border-workspace-accent bg-workspace-accent/10 flex items-center justify-center gap-2 text-xs font-semibold text-workspace-accent transition-all duration-150 animate-in fade-in select-none shadow-xs"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-workspace-accent animate-ping" />
                                <span>Drop {itemSingular} here</span>
                              </div>
                            )}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Columns / Stages Tab Footer */}
              <DialogFooter className="mt-4 pt-3 border-t border-border flex items-center justify-end">
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </TabsContent>

            {/* TAB: FIELD SETTINGS (BLANK TAB FOR PIPELINES) */}
            {isStage && (
              <TabsContent value="fields" className="mt-4 focus-visible:outline-none">
                <div className="flex flex-col items-center justify-center py-16 px-4 text-center border border-dashed border-border/80 rounded-xl bg-muted/10">
                  <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mx-auto mb-3 text-muted-foreground">
                    <Sliders className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">Field Settings</h3>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    Configure custom fields, default properties, and deal attributes for this pipeline.
                  </p>
                </div>
                <DialogFooter className="mt-6 pt-3 border-t border-border flex items-center justify-end">
                  <Button variant="secondary" onClick={() => setOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </TabsContent>
            )}

            {/* TAB: TAGS (BLANK TAB FOR PIPELINES) */}
            {isStage && (
              <TabsContent value="tags" className="mt-4 focus-visible:outline-none">
                <div className="flex flex-col items-center justify-center py-16 px-4 text-center border border-dashed border-border/80 rounded-xl bg-muted/10">
                  <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mx-auto mb-3 text-muted-foreground">
                    <Tag className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">Tags</h3>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    Create and manage tags to organize and label deals in this pipeline.
                  </p>
                </div>
                <DialogFooter className="mt-6 pt-3 border-t border-border flex items-center justify-end">
                  <Button variant="secondary" onClick={() => setOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete Column / Stage Confirmation Alert Dialog */}
      <AlertDialog open={!!columnToDelete} onOpenChange={(isOpen) => !isOpen && setColumnToDelete(null)}>
        <AlertDialogContent className="bg-card border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete {ItemSingular}</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {columnToDelete?.taskCount ? (
                <>
                  {ItemSingular} <strong className="text-foreground">{columnToDelete.title}</strong> currently contains{" "}
                  <strong className="text-foreground">{columnToDelete.taskCount} {columnToDelete.taskCount === 1 ? unitSingular : unitPlural}</strong>.
                  Deleting this {itemSingular} will permanently delete the {itemSingular} and all {unitPlural} inside it. Are you sure you want to proceed?
                </>
              ) : (
                <>
                  Are you sure you want to delete the {itemSingular} <strong className="text-foreground">{columnToDelete?.title}</strong>? This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingColumn}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeletingColumn}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingColumn ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting...
                </>
              ) : (
                `Delete ${ItemSingular}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
